/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, forwardRef, useEffect } from 'react';
// Helpers
import cn from 'classnames';
import isNil from 'lodash/isNil';
import { toMaxFixed } from 'src/shared/helpers/formatting';
// Components
import GridColumnsContext from './GridColumnsContext';
import { Grid, GridColumn, GridToolbar } from '@gulfwinds/ui-components';
import { Tooltip } from '@progress/kendo-react-tooltip';
import {
  BaseCell, ActionsCell as ActionsCellComponent, BooleanCell, PercentCell, DateCell, DateTimeCell, CheckboxCell, PriceCell,
} from '../Cells';
import { NotSortableHeaderCell } from '../Headers';
import RowDetails from './RowDetails';
import KeyValue from 'shared/layout/KeyValue';
import LineLoader from 'shared/indicators/LineLoader';
// Hooks
import { useClientSidePagination } from '../hooks/useClientSidePagination';
// Styles
import './style.scss';
import { theme } from 'src/theme';
import { emptyObject, returnEmptyObject } from 'src/shared/helpers/utils';
import { GridNoRecords } from '@progress/kendo-react-grid';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const EXPAND_FIELD = 'expanded';
const GROUP_ID_FIELD = 'groupId';

/**
 * BaseGrid component.
 *
 * @typedef {object} BaseGridProps
 * @property {Array} data - The data for the grid.
 * @property {string} className - Additional CSS class name for styling.
 * @property {Array} columns - Configuration for grid columns.
 * @property {function} onRowSelect - Callback when a row is selected.
 * @property {number} page - Current page number.
 * @property {boolean} pageable - Indicates if pagination is enabled.
 * @property {number} pagingButtonCount - Number of pagination buttons to display.
 * @property {ReactNode} children - Additional child components.
 * @property {ReactNode} actionBarContent - Content for the action bar.
 * @property {boolean} hideActionBar - Whether to hide the action bar.
 * @property {function} onShowRowHistory - Callback for showing row history.
 * @property {boolean} responsive - Enable responsive behavior.
 * @property {boolean} readOnly - Indicates if the grid is read-only (default: false).
 * @property {boolean} hideActionsCell - Hide actions cell.
 * @property {object} actionCellProps - Additional props for the actions cell.
 * @property {function} getActionCellProps - Function to get additional props for the actions cell from data item props.
 * @property {boolean} hideEditButton - Hide edit button.
 * @property {number} actionsCellWidth - Width of the actions cell.
 * @property {function} onEnterEdit - Callback when entering edit mode.
 * @property {function} onCancel - Callback when edit mode is canceled.
 * @property {function} onItemDelete - Callback when an item is deleted.
 * @property {function} onEnterHistory - Callback when viewing history.
 * @property {function} onEnable - Callback when enabling an item.
 * @property {function} onDisable - Callback when disabling an item.
 * @property {string} itemEnabledField - Field name for item enable/disable status.
 * @property {string} itemName - Name of the item.
 * @property {string} expandField - Field name for expanded rows.
 * @property {boolean} defaultExpanded - Default expanded state.
 * @property {function} detail - Function to render row details.
 * @property {object} extraDetailProps - Additional props for row details.
 * @property {string} identifierColumn - Column name for unique identifiers.
 * @property {function} cellRender - Custom cell rendering function.
 * @property {function} onDataStateChange - Callback for data state changes.
 * @property {object} dataState - Current data state.
 * @property {function} process - Data processing function.
 * @property {string} editField - Field name for edit mode.
 * @property {object} searchParams - Search parameters.
 * @property {boolean} restored - Indicates if data is restored.
 * @property {boolean} groupable - Enable grouping (default: false).
 * @property {object} group - Grouping configuration.
 * @property {number} resultCount - Total result count.
 * @property {boolean} showResultCount - Show result count.
 * @property {boolean} showSelectedCount - Show selected count.
 * @property {number} selectedCount - Number of selected items.
 * @property {function} refreshGrid - Callback to refresh the grid.
 * @property {boolean} forceReload - Force data reload.
 * @property {function} onExpandedIndexesChanged - Callback for expanded indexes.
 * @property {Array} forcedExpandedIndexes - Forced expanded indexes.
 * @property {string} allowModificationField - Field for modification permission.
 * @property {string} modificationDisabledMessage - Message when modification is disabled.
 * @property {boolean} isBgLoading - Indicates if background loading is active.
 * @property {number} subTotal - Subtotal value.
 * @property {string} subTotalText - Text for the subtotal.
 * @property {...any} props - Additional props.
 * @property {string} noDataMessage - Text to show when the grid data is empty.
 *
 * @param {BaseGridProps} props - Component props.
 * @param {React.Ref} ref - React ref.
 * @returns {React.ReactElement} - BaseGrid component.
 */
const BaseGrid = forwardRef(
  (
    {
      id,
      data,
      className,
      columns,
      onRowSelect,
      page,
      pageable,
      pagingButtonCount,
      children,
      actionBarContent,
      hideActionBar,
      onShowRowHistory,
      responsive,
      readOnly = false,
      // actions cell
      hideActionsCell,
      ActionsCell = ActionsCellComponent,
      actionCellProps = emptyObject,
      getActionCellProps = returnEmptyObject,
      hideEditButton,
      actionsCellWidth,
      onEnterEdit,
      onCancel,
      onItemDelete,
      onEnterHistory,
      onEnable,
      onDisable,
      itemEnabledField = 'isEnabled',
      itemName,
      // details
      expandField = EXPAND_FIELD,
      defaultExpanded = false,
      detail,
      extraDetailProps,
      identifierColumn,
      cellRender,
      onDataStateChange,
      dataState,
      process,
      editField,
      // search params
      searchParams,
      restored,
      groupable = false,
      group,
      resultCount,
      showResultCount,
      showSelectedCount,
      selectedCount,
      refreshGrid,
      forceReload,
      onExpandedIndexesChanged,
      forcedExpandedIndexes,
      allowModificationField = null,
      modificationDisabledMessage = null,
      isBgLoading,
      subTotal,
      subTotalText,
      autoClientSidePagination = false,
      tooltipPosition,
      tooltipAnchorElement = 'target',
      noDataMessage = 'No Data Found',
      ...props
    },
    ref,
  ) => {
    const isGroup = (dataItem) => GROUP_ID_FIELD in dataItem;

    const {
      page: clientPage,
      pageData: clientPageData,
      pageSize: clientPageSize,
      total: clientPageTotal,
      onPageChanged: onClientPageChanged,
    } = useClientSidePagination(autoClientSidePagination, data, {
      page,
      take: pageable?.pageSize,
    });

    const hasNestedGroups = (dataItem) => isGroup(dataItem) && 'items' in dataItem;
    const getDataItemId = (dataItem) => {
      const key = isGroup(dataItem) ? GROUP_ID_FIELD : identifierColumn;
      return dataItem[key];
    };

    const initializeExpandedIndexes = (data, indexes = []) => {
      data.forEach((element) => {
        const id = getDataItemId(element);
        if (defaultExpanded && id) {
          indexes.push(id);
        }

        if (hasNestedGroups(element)) {
          initializeExpandedIndexes(element.items, indexes);
        }
      });
    };

    const getDefaultExpandedIndexes = (data) => {
      if (forcedExpandedIndexes && forcedExpandedIndexes?.length) {
        return forcedExpandedIndexes;
      }

      const indexes = [];
      initializeExpandedIndexes(data, indexes);
      return indexes;
    };

    // details
    const [expandedIndexes, setExpandedIndexes] = useState(getDefaultExpandedIndexes(data));
    const isEnableDisableActive = useMemo(() => onEnable && onDisable, [onEnable, onDisable]);

    useEffect(() => {
      if (forcedExpandedIndexes) {
        setExpandedIndexes(forcedExpandedIndexes);
      }
    }, [forcedExpandedIndexes]);

    const DetailComponent = responsive ? detail ?? RowDetails : detail;
    const innerDetail = useMemo(() => (
      DetailComponent
        ? (detailProps) => (
          <DetailComponent
            {...props}
            {...detailProps}
            {...extraDetailProps}
            searchParams={searchParams}
            restored={restored}
          />
        ) : undefined),
    [searchParams, restored, detail]);

    const onExpandChange = ({ dataItem }) => {
      const expanded = EXPAND_FIELD in dataItem ? !dataItem[expandField] : !defaultExpanded;
      const id = getDataItemId(dataItem);

      const newExpandedIndexes = (expanded ? [...expandedIndexes, id] : expandedIndexes.filter((p) => p !== id));
      setExpandedIndexes(newExpandedIndexes);
      if (onExpandedIndexesChanged) {
        onExpandedIndexesChanged(newExpandedIndexes);
      }
    };

    // row select
    const selectable = !isNil(onRowSelect);
    const onSelectionChange = ({ dataItems, ...e }) => {
      if (selectable) {
        const selectedDataItem = dataItems[e.endRowIndex];
        onRowSelect(selectedDataItem, e);
      }
    };

    const setExpandedState = (data) => data.map((x) => (
      hasNestedGroups(x)
        ? {
          ...x,
          [expandField]: expandedIndexes.includes(getDataItemId(x)),
          items: setExpandedState(x.items),
        }
        : {
          ...x,
          [expandField]: expandedIndexes.includes(getDataItemId(x)),
        }));

    // data
    const innerData = useMemo(() => setExpandedState(data), [data, expandedIndexes]);

    // grid
    const buildColumns = (columns = []) => (
      hideActionsCell
        ? columns
        : [
          ...columns,
          {
            title: 'Actions',
            isSystem: true,
            width: actionsCellWidth ?? (isEnableDisableActive ? 250 : 175),
            avoidSort: true,
            sortable: false,
            hide: hideActionsCell || readOnly,
            cell: (props) => {
              const { dataItem, dataIndex } = props;
              return (
                <ActionsCell
                  id={`${id ?? ''}${id && dataItem[identifierColumn] ? '-' : ''}${dataItem[identifierColumn] ?? ''}`}
                  editField={editField}
                  dataItem={dataItem}
                  itemEnabledField={itemEnabledField}
                  allowModificationField={allowModificationField}
                  modificationDisabledMessage={modificationDisabledMessage}
                  {...(onEnterEdit ? { onEnterEdit: () => onEnterEdit(dataItem) } : {})}
                  {...(onCancel ? { onCancel: () => onCancel(dataItem, dataIndex) } : {})}
                  {...(onItemDelete ? { onDelete: () => onItemDelete(dataItem) } : {})}
                  {...(onEnterHistory ? { onEnterHistory: () => onEnterHistory(dataItem) } : {})}
                  {...(onEnable ? { onEnable: () => onEnable(dataItem) } : {})}
                  {...(onDisable ? { onDisable: () => onDisable(dataItem) } : {})}
                  readOnly={readOnly}
                  hideEditButton={hideEditButton}
                  itemName={itemName}
                  {...actionCellProps}
                  {...getActionCellProps(props)}
                />
              );
            },
          },
        ]);

    const innerColumns = buildColumns(columns);

    const gridClassName = cn('base-grid', {
      responsive,
      [className]: className,
      'has-expanded-details': expandedIndexes && expandedIndexes.length > 0,
    });

    const rowRender = (trElement, { dataItem }) => {
      const itemIsDisabled = Object.keys(dataItem).includes(itemEnabledField) && !dataItem[itemEnabledField];
      const disabledClassName = itemIsDisabled ? 'disabled' : '';
      const className = cn(trElement.props.className, disabledClassName);

      const trProps = {
        className,
      };
      return React.cloneElement(trElement, { ...trProps }, trElement.props.children);
    };
    const SearchResultCount = ({ count, showResultCount }) => {
      if (count < 0 || !showResultCount) {
        return null;
      }

      return (
        <div className='grid-total-count'>
          <KeyValue
            keyName='Total'
            value={`${count} item${count === 1 ? '' : 's'}`}
          />
          {(showSelectedCount && selectedCount > 0) && (
            <>
              <span className='grid-total-div'>|</span>
              <KeyValue keyName='Selected' value={`${selectedCount} items`} />
            </>
          )}
          {(subTotal > 0) && (
            <>
              <span className='grid-total-div'>|</span>
              <KeyValue keyName={subTotalText ?? 'Sub Total'} value={subTotal} />
            </>
          )}
        </div>
      );
    };

    SearchResultCount.defaultProps = {
      showResultCount: true,
      count: 0,
    };

    return (
      <GridColumnsContext.Provider value={innerColumns}>
        {(!hideActionBar && (actionBarContent || showResultCount)) && (
          <GridToolbar>
            <SearchResultCount count={autoClientSidePagination ? clientPageTotal : resultCount} showResultCount={showResultCount} />
            {actionBarContent}
          </GridToolbar>
        )}
        <Tooltip openDelay={100} anchorElement={tooltipAnchorElement} position={tooltipPosition} parentTitle="true">
          {!isNil(isBgLoading) && <LineLoader active={isBgLoading} />}
          <Grid
            ref={ref}
            data={autoClientSidePagination ? clientPageData : innerData}
            skip={autoClientSidePagination ? clientPage?.skip : page?.skip}
            pageSize={autoClientSidePagination ? clientPage?.pageSize : page?.take}
            pageable={
              isNil(pageable)
                ? {
                  buttonCount: pagingButtonCount ?? 5,
                  info: true,
                  type: 'numeric',
                  pageSizes: [10, 25, 50, 100],
                  previousNext: true,
                  pageSizeValue: autoClientSidePagination ? clientPageSize : undefined,
                }
                : pageable
            }
            className={gridClassName}
            selectable={selectable}
            onSelectionChange={onSelectionChange}
            detail={innerDetail}
            expandField={expandField}
            onExpandChange={onExpandChange}
            cellRender={cellRender}
            onDataStateChange={onDataStateChange}
            editField={editField}
            rowRender={rowRender}
            groupable={groupable}
            group={group}
            {...props}
            take={autoClientSidePagination ? clientPage?.take : props?.take}
            total={autoClientSidePagination ? clientPageTotal : props?.total}
            onPageChange={autoClientSidePagination ? onClientPageChanged : props?.onPageChange}
          >
            <GridNoRecords>
              {noDataMessage}
            </GridNoRecords>
            {children}
            {innerColumns.map(({ isDetail, mobileOnly, ...c }) => {
              const props = c;
              const {
                field,
                className,
                headerClassName,
                isPercent,
                isDate,
                isDateTime,
                isFloat,
                significantDigits = 2,
                formatMethod,
                isBoolean,
                isCurrency,
                avoidSort,
                sortable,
                hide,
                filterable,
                ignoreTimezone,
                ignoreSeconds,
                roundUp,
                minimumFractionDigits,
                maximumFractionDigits,
              } = c;

              const hideClassName = isDetail ? 'hide-on-mob' : mobileOnly ? 'hide-on-web' : null;
              const columnClassName = cn(className, { [hideClassName]: !isNil(hideClassName) });
              const columnHeaderClassName = cn(headerClassName, { [hideClassName]: !isNil(hideClassName) });

              if (isPercent) {
                props.cell = ({ dataItem, ...props }) => (
                  <PercentCell value={dataItem[field]} roundUp={roundUp} {...props} />
                );
              } else if (isDateTime) {
                props.cell = ({ dataItem, ...props }) => (
                  <DateTimeCell
                    date={dataItem[field]}
                    formatMethod={formatMethod}
                    ignoreTimezone={ignoreTimezone}
                    ignoreSeconds={ignoreSeconds}
                    {...props}
                  />
                );
              } else if (isDate) {
                props.cell = ({ dataItem, ...props }) => (
                  <DateCell
                    date={dataItem[field]}
                    formatMethod={formatMethod}
                    {...props}
                  />
                );
              } else if (isCurrency) {
                props.cell = (props) => (
                  <PriceCell
                    {...props}
                    minimumFractionDigits={minimumFractionDigits}
                    maximumFractionDigits={maximumFractionDigits}
                  />
                );
              } else if (isBoolean) {
                const { trueText, falseText } = props;
                props.cell = ({ dataItem, field, ...props }) => (
                  dataItem[editField] ? (
                    <CheckboxCell dataItem={dataItem} {...props} />
                  ) : (
                    <BooleanCell
                      value={dataItem[field]}
                      {...(isNil(trueText) ? {} : { trueText })}
                      {...(isNil(falseText) ? {} : { falseText })}
                      {...props}
                    />
                  ));
              } else if (isFloat) {
                props.cell = ({ dataItem, field, ...props }) => (
                  <BaseCell {...props}>
                    {toMaxFixed(dataItem[field], significantDigits) ?? dataItem[field]}
                  </BaseCell>
                );
              }

              if (avoidSort || sortable === false) {
                props.headerCell = props.headerCell ?? ((props) => <NotSortableHeaderCell {...props} />);
              }
              return (
                !hide && (
                  <GridColumn
                    key={`column_${field}`}
                    headerClassName={columnHeaderClassName}
                    className={columnClassName}
                    filterable={filterable}
                    {...props}
                  />
                )
              );
            })}
          </Grid>
        </Tooltip>
      </GridColumnsContext.Provider>
    );
  },
);

BaseGrid.displayName = 'BaseGrid';

export default BaseGrid;
